import uuid from 'react-uuid';
import { defaultTextNewItem } from '../content/default.text';
import { deepCloneArray } from '../utils/array';
import { getId, getItemPicAsset } from '../utils/item';
import axios from 'axios';
import store from '../store/store';
import { saveAll } from '../store/items';
import {
  createDefaultGeneratorConfig,
  sanitizeGeneratorConfigForStorage,
  sanitizeGeneratorEntriesForStorage,
} from '../utils/generator';

const mergeItemGeneratorsInStore = (itemId, generators) => {
  if (!itemId) {
    return;
  }
  const state = store.getState();
  const currentItem = state?.items?.[itemId];
  if (!currentItem?._id) {
    return;
  }
  const nextGenerators = Array.isArray(generators) ? generators : [];
  store.dispatch(
    saveAll({
      item: {
        ...currentItem,
        generators: nextGenerators,
      },
      log: 'generators-sync',
    }),
  );
};

const DEFAULT_APP_AVATAR_URL = `${process.env.PUBLIC_URL || ''}/logo512.png`;

const hasAvatarVisual = asset =>
  Boolean(asset?.url) || Boolean(asset?.iconSlug);

const buildAvatarPicEntry = (picId, asset) => ({
  [picId]: {
    url: asset?.url ?? null,
    iconSlug: asset?.iconSlug ?? null,
    bgColor: asset?.bgColor ?? null,
    x: 0,
    y: 0,
    w: 1,
    h: 1,
    i: picId,
  },
});

export const resolveDefaultAvatarAssetForNewItem = (
  currentItem,
  items = {},
  fallbackAsset = { url: DEFAULT_APP_AVATAR_URL },
) => {
  const appAsset = {
    url: fallbackAsset?.url ?? DEFAULT_APP_AVATAR_URL,
    iconSlug: fallbackAsset?.iconSlug ?? null,
    bgColor: fallbackAsset?.bgColor ?? null,
  };

  const universeId =
    currentItem?.type === 'universe' ? currentItem?._id : currentItem?.universe;
  const folderId =
    currentItem?.type === 'campaign' ? currentItem?._id : currentItem?.campaign;
  const universeFromStore = universeId ? items?.[universeId] || null : null;
  const folderFromStore = folderId ? items?.[folderId] || null : null;
  const universe =
    currentItem?.type === 'universe'
      ? universeFromStore || currentItem
      : universeFromStore;
  const folder =
    currentItem?.type === 'campaign'
      ? folderFromStore || currentItem
      : folderFromStore;

  const universeAsset = getItemPicAsset(universe, 0);
  const folderAsset = getItemPicAsset(folder, 0);

  if (hasAvatarVisual(folderAsset)) {
    return folderAsset;
  }
  if (hasAvatarVisual(universeAsset)) {
    return universeAsset;
  }
  return appAsset;
};

export const getItemGenerators = async itemId => {
  if (!itemId) {
    return [];
  }
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/items/${itemId}/generators`);
  const generators = Array.isArray(res?.data) ? res.data : [];
  mergeItemGeneratorsInStore(itemId, generators);
  return generators;
};

export const upsertItemGenerator = async (itemId, generatorId, config) => {
  if (!itemId || !generatorId) {
    return null;
  }
  const payloadConfig =
    config && typeof config === 'object'
      ? sanitizeGeneratorConfigForStorage(config)
      : {};
  await axios.put(
    `${process.env.REACT_APP_API_URL}/items/${itemId}/generators/${generatorId}`,
    { config: payloadConfig },
  );
  const state = store.getState();
  const currentItem = state?.items?.[itemId];
  const currentGenerators = Array.isArray(currentItem?.generators)
    ? deepCloneArray(currentItem.generators)
    : [];
  const idx = currentGenerators.findIndex(entry => getId(entry) === generatorId);
  const nextEntry = { [generatorId]: payloadConfig };
  if (idx === -1) {
    currentGenerators.push(nextEntry);
  } else {
    currentGenerators[idx] = nextEntry;
  }
  mergeItemGeneratorsInStore(itemId, currentGenerators);
  return nextEntry;
};

export const createItemGenerator = async (itemId, generatorId, config) => {
  if (!itemId || !generatorId) {
    return null;
  }
  const payloadConfig =
    config && typeof config === 'object'
      ? sanitizeGeneratorConfigForStorage(config)
      : {};
  await axios.post(`${process.env.REACT_APP_API_URL}/items/${itemId}/generators`, {
    generatorId,
    config: payloadConfig,
  });
  const state = store.getState();
  const currentItem = state?.items?.[itemId];
  const currentGenerators = Array.isArray(currentItem?.generators)
    ? deepCloneArray(currentItem.generators)
    : [];
  currentGenerators.push({ [generatorId]: payloadConfig });
  mergeItemGeneratorsInStore(itemId, currentGenerators);
  return { [generatorId]: payloadConfig };
};

/**
 * Use to create a link between two items
 * @param {string} linkId
 * @param {string} itemId
 */
const createLink = async (linkId, itemId) => {
  const linkUrl = `items/${linkId}`;
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/${linkUrl}`);
  const cloneLinkedLink = deepCloneArray(res.data.links);
  cloneLinkedLink.push({
    [itemId]: { id: itemId, x: 0, y: 0, w: 4, h: 1, i: itemId, label: '' },
  });
  update(linkUrl, 'links', cloneLinkedLink);
};

/**
 *
 * @param {string} itemId
 * @param {function} callback
 */
export const deleteItem = (itemId, callback) => {
  const deleteUrl = `items/${itemId}`;
  axios.delete(`${process.env.REACT_APP_API_URL}/${deleteUrl}`).then(res => {
    const deletedIds = Array.isArray(res?.data?.deletedIds)
      ? res.data.deletedIds
      : [itemId];
    if (typeof callback === 'function') {
      callback(deletedIds);
    }
  });
};

export const deleteItemWithChildren = async (item, callback) => {
  const itemId = item?._id;
  if (!itemId) {
    if (typeof callback === 'function') {
      callback([]);
    }
    return;
  }

  const res = await axios.delete(`${process.env.REACT_APP_API_URL}/items/${itemId}`);
  const deletedIds = Array.isArray(res?.data?.deletedIds)
    ? res.data.deletedIds
    : [itemId];

  if (typeof callback === 'function') {
    callback(deletedIds);
  }
};

/**
 *
 * @param {array} tabs
 * @param {object} item
 */
const dupplicateTabs = async (tabs, item) => {
  if (!Array.isArray(tabs) || !item?._id) {
    return null;
  }
  const defaultTabIds = new Set(['text', 'img', 'link', 'num', 'conditions']);
  const tabConfigById = new Map();
  for (const tabObj of tabs) {
    const tabId = getId(tabObj);
    tabConfigById.set(tabId, tabObj?.[tabId] || {});
  }
  const duplicatedTabs = [];
  const tabIdMap = new Map();
  for (const tabObj of tabs) {
    const tabId = getId(tabObj);
    if (!tabId || defaultTabIds.has(tabId)) {
      continue;
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/items/${tabId}`,
      );
      if (!res.data.error) {
        const tab = res.data;
        if (tab?.grid) {
          const newGrid = tab.grid.map(elementObj => {
            return {
              [getId(elementObj)]: {
                ...elementObj[getId(elementObj)],
                item: item._id,
              },
            };
          });
          const resTab = await createNewItem(
            {
              ...tab,
              grid: newGrid,
              alias: `de ${item.label}`,
              callback: () => null,
            },
            item,
          );
          if (resTab?.data?.item) {
            duplicatedTabs.push(resTab?.data?.item);
            tabIdMap.set(tabId, resTab?.data?.item);
            if (item.sidebar === tab._id) {
              update(`items/${item._id}`, 'sidebar', resTab?.data?.item._id);
            }
          }
        }
      }
    } catch (e) {
      // Ignore tabs that are not real item-backed tabs (e.g. default tabs).
      continue;
    }
  }

  const generatTabs = tabs
    .map(tabObj => {
      const oldTabId = getId(tabObj);
      const duplicatedTab = tabIdMap.get(oldTabId);
      if (!duplicatedTab) {
        return null;
      }
      const originalConfig = tabConfigById.get(oldTabId) || {};
      return {
        [duplicatedTab._id]: {
          ...originalConfig,
          _id: duplicatedTab._id,
          label: duplicatedTab.label ?? originalConfig.label,
          display: originalConfig.display ?? 'grid',
        },
      };
    })
    .filter(Boolean);
  const clonedTabs = Array.isArray(item.tabs) ? deepCloneArray(item.tabs) : [];
  const nextTabs = [...generatTabs, ...clonedTabs];
  await update(`items/${item._id}`, 'tabs', nextTabs);
  return nextTabs;
};

const getAutoLinkedTabEntries = sourceItem => {
  if (!Array.isArray(sourceItem?.tabs)) {
    return [];
  }
  return sourceItem.tabs
    .map(tabObj => {
      const id = getId(tabObj);
      const payload = tabObj?.[id];
      if (!id || !payload) return null;
      if (payload?.addToLinkedItems !== true) return null;
      return { id, payload };
    })
    .filter(Boolean);
};

const upsertTabsToItem = async (targetItem, tabEntries = []) => {
  if (!targetItem?._id || tabEntries.length === 0) {
    return;
  }
  const currentTabs = Array.isArray(targetItem.tabs)
    ? deepCloneArray(targetItem.tabs)
    : [];
  let changed = false;
  tabEntries.forEach(entry => {
    const exists = currentTabs.some(tabObj => getId(tabObj) === entry.id);
    if (exists) return;
    const copiedPayload = { ...(entry.payload || {}) };
    delete copiedPayload.addToLinkedItems;
    currentTabs.push({ [entry.id]: copiedPayload });
    changed = true;
  });
  if (changed) {
    await update(`items/${targetItem._id}`, 'tabs', currentTabs);
  }
};

const syncAutoTabsToLinkedItems = async (sourceItem, linkedItemIds = []) => {
  if (!sourceItem?._id || !Array.isArray(linkedItemIds) || linkedItemIds.length === 0) {
    return;
  }
  const entries = getAutoLinkedTabEntries(sourceItem);
  if (entries.length === 0) return;

  const state = store.getState();
  await Promise.all(
    linkedItemIds.map(async linkedId => {
      if (!linkedId || linkedId === sourceItem._id) return;
      const cached = state?.items?.[linkedId];
      const linkedItem =
        cached && !cached.__partial
          ? cached
          : (await axios.get(`${process.env.REACT_APP_API_URL}/items/${linkedId}`))
              ?.data;
      if (!linkedItem || linkedItem?.error) return;
      await upsertTabsToItem(linkedItem, entries);
    }),
  );
};

/**
 *
 * @param {object} params
 * @param {object} currentItem
 */
export const dupplicateItem = async (
  params = {
    label: 'new item',
    callback: () => null,
  },
  currentItem,
) => {
  const sourceItemId = params?._id;
  let sourceItem = params;
  if (sourceItemId) {
    const cached = store.getState()?.items?.[sourceItemId];
    const hasCompleteCachedShape =
      cached &&
      !cached.__partial &&
      Array.isArray(cached?.tabs) &&
      Array.isArray(cached?.pics) &&
      Array.isArray(cached?.text) &&
      Array.isArray(cached?.has_num) &&
      Array.isArray(cached?.conditions) &&
      Array.isArray(cached?.generators || []);

    if (hasCompleteCachedShape) {
      sourceItem = cached;
    } else {
      try {
        const resFull = await axios.get(
          `${process.env.REACT_APP_API_URL}/items/${sourceItemId}`,
        );
        if (resFull?.data && !resFull.data.error) {
          sourceItem = resFull.data;
        }
      } catch (e) {
        // Fallback to the provided payload if we cannot fetch the full source item.
        sourceItem = params;
      }
    }
  }

  const sourceItemWithOverrides = {
    ...sourceItem,
    label: params?.label ?? sourceItem?.label,
    callback: params?.callback ?? sourceItem?.callback,
  };

  const {
    pics,
    has_num,
    text,
    conditions,
    filter,
    tabs,
    timelines,
    dices,
    generators,
  } = sourceItemWithOverrides;
  const newObjItem = {
    ...sourceItemWithOverrides,
    pics,
    has_num,
    dices,
    text,
    conditions,
    filter,
    tabs: null,
    timelines,
    generators,
  };
  const res = await createNewItem(newObjItem, currentItem);
  if (res?.data?.item) {
    const createdItem = res.data.item;
    const nextTabs = await dupplicateTabs(tabs, createdItem);
    const createdItemWithTabs = nextTabs
      ? { ...createdItem, tabs: nextTabs }
      : createdItem;
    const linkedIds = Array.isArray(createdItemWithTabs?.links)
      ? createdItemWithTabs.links.map(linkObj => getId(linkObj)).filter(Boolean)
      : [];
    await syncAutoTabsToLinkedItems(createdItemWithTabs, linkedIds);
    if (sourceItemId) {
      const remapFilterIds = (filtersList, newId) => {
        if (!Array.isArray(filtersList)) {
          return filtersList;
        }
        return filtersList.map(filterObj => {
          const filterId = getId(filterObj);
          const filterData = filterObj?.[filterId];
          if (!filterData) {
            return filterObj;
          }
          const nextItems = Array.isArray(filterData.items)
            ? filterData.items.map(id => (id === sourceItemId ? newId : id))
            : filterData.items;
          const nextExclude = Array.isArray(filterData.exclude)
            ? filterData.exclude.map(id => (id === sourceItemId ? newId : id))
            : filterData.exclude;
          return {
            ...filterObj,
            [filterId]: {
              ...filterData,
              items: nextItems,
              exclude: nextExclude,
            },
          };
        });
      };

      let timelinesChanged = false;
      const remappedTimelines = (res?.data?.item?.timelines || []).map(
        timelineObj => {
          const timelineId = getId(timelineObj);
          const timelineData = timelineObj?.[timelineId];
          if (!timelineData || !Array.isArray(timelineData.entries)) {
            return timelineObj;
          }
          const nextEntries = timelineData.entries.map(entry => {
            if (entry?.itemId === sourceItemId) {
              timelinesChanged = true;
              return { ...entry, itemId: res.data.item._id };
            }
            return entry;
          });
          return {
            ...timelineObj,
            [timelineId]: {
              ...timelineData,
              entries: nextEntries,
            },
          };
        },
      );
      if (timelinesChanged) {
        update(`items/${res.data.item._id}`, 'timelines', remappedTimelines);
      }

      if (Array.isArray(res?.data?.item?.filters)) {
        const remappedFilters = remapFilterIds(
          res.data.item.filters,
          res.data.item._id
        );
        update(`items/${res.data.item._id}`, 'filters', remappedFilters);
      }
    }
  }
};

export const copyIntoItem = async (currentItem, itemToCopy) => {
  const { pics, has_num, text, conditions, dices, generators } = currentItem;
  const newObjItem = {
    ...currentItem,
    pics: [...itemToCopy.pics, ...pics],
    has_num: [...itemToCopy.has_num, ...has_num],
    text: [...itemToCopy.text, ...text],
    conditions: [...itemToCopy.conditions, ...conditions],
    dices: [...(itemToCopy.dices || []), ...(dices || [])],
    generators: [...(itemToCopy.generators || []), ...(generators || [])],
  };

  await update(`items/${currentItem._id}`, 'pics', newObjItem.pics);
  await update(`items/${currentItem._id}`, 'has_num', newObjItem.has_num);
  await update(`items/${currentItem._id}`, 'text', newObjItem.text);
  await update(`items/${currentItem._id}`, 'conditions', newObjItem.conditions);
  await update(`items/${currentItem._id}`, 'dices', newObjItem.dices);
  await update(`items/${currentItem._id}`, 'generators', newObjItem.generators);

  dupplicateTabs(itemToCopy.tabs, currentItem);
};

const deepClone = value => {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
};

const DEFAULT_TAB_IDS = new Set(['text', 'img', 'link', 'num', 'conditions']);

const isValidObjectId = id => typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);

const remapIdsDeep = (value, idMap) => {
  if (!idMap || idMap.size === 0) {
    return value;
  }
  if (typeof value === 'string') {
    return idMap.get(value) || value;
  }
  if (Array.isArray(value)) {
    return value.map(entry => remapIdsDeep(entry, idMap));
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, entry]) => {
      const nextKey = idMap.get(key) || key;
      acc[nextKey] = remapIdsDeep(entry, idMap);
      return acc;
    }, {});
  }
  return value;
};

const getCachedOrFetchItem = async itemId => {
  if (!itemId) return null;
  const cached = store.getState()?.items?.[itemId];
  if (cached && !cached.__partial) {
    return cached;
  }
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/items/${itemId}`);
    return res?.data && !res.data.error ? res.data : null;
  } catch (error) {
    return null;
  }
};

const getCustomTabIds = item => {
  if (!Array.isArray(item?.tabs)) {
    return [];
  }
  return item.tabs.map(tabObj => getId(tabObj)).filter(id => id && !DEFAULT_TAB_IDS.has(id));
};

const getReferencedItemIds = item => {
  const ids = new Set();
  const add = id => {
    if (isValidObjectId(id)) {
      ids.add(id);
    }
  };

  if (Array.isArray(item?.links)) {
    item.links.forEach(linkObj => add(getId(linkObj)));
  }
  if (Array.isArray(item?.filters)) {
    item.filters.forEach(filterObj => {
      const filterId = getId(filterObj);
      const filterData = filterObj?.[filterId];
      if (Array.isArray(filterData?.items)) {
        filterData.items.forEach(add);
      }
      if (Array.isArray(filterData?.exclude)) {
        filterData.exclude.forEach(add);
      }
    });
  }
  if (Array.isArray(item?.timelines)) {
    item.timelines.forEach(timelineObj => {
      const timelineId = getId(timelineObj);
      const timelineData = timelineObj?.[timelineId];
      if (Array.isArray(timelineData?.entries)) {
        timelineData.entries.forEach(entry => add(entry?.itemId));
      }
    });
  }
  if (Array.isArray(item?.grid)) {
    item.grid.forEach(gridObj => {
      const elementId = getId(gridObj);
      const element = gridObj?.[elementId];
      if (element?.type === 'link') {
        add(elementId);
      }
      add(element?.item);
    });
  }

  return Array.from(ids);
};

const collectTemplateCloneSources = async templateItem => {
  const sourcesById = new Map();
  const queue = [{ id: templateItem?._id, forceClone: true, item: templateItem }];
  const visited = new Set();

  while (queue.length > 0) {
    const next = queue.shift();
    const itemId = next?.id;
    if (!itemId || visited.has(itemId)) {
      continue;
    }
    visited.add(itemId);

    const item = next.item || (await getCachedOrFetchItem(itemId));
    if (!item?._id) {
      continue;
    }

    const shouldClone = next.forceClone || item.isTemplate === true;
    if (!shouldClone) {
      continue;
    }

    sourcesById.set(item._id, item);

    getCustomTabIds(item).forEach(tabId => {
      queue.push({ id: tabId, forceClone: true });
    });

    const referencedIds = getReferencedItemIds(item);
    for (const referencedId of referencedIds) {
      if (visited.has(referencedId)) {
        continue;
      }
      const referencedItem = await getCachedOrFetchItem(referencedId);
      if (referencedItem?.isTemplate === true) {
        queue.push({ id: referencedId, forceClone: true, item: referencedItem });
      }
    }
  }

  return sourcesById;
};

const prepareTemplateCloneParams = (sourceItem, overrides = {}) => {
  const clone = deepClone(sourceItem) || {};
  delete clone._id;
  delete clone.owner;
  delete clone.createdAt;
  delete clone.updatedAt;
  return {
    ...clone,
    ...overrides,
    isTemplate: false,
    templateSourceId: sourceItem?._id,
    links: [],
    tabs: null,
  };
};

const REMAPPED_TEMPLATE_FIELDS = [
  'links',
  'tabs',
  'sidebar',
  'grid',
  'filters',
  'timelines',
];

export const createItemFromTemplate = async (
  templateItem,
  targetParentItem,
  options = {},
) => {
  const templateId = templateItem?._id;
  if (!templateId) {
    return null;
  }

  const rootTemplate = (await getCachedOrFetchItem(templateId)) || templateItem;
  if (!rootTemplate?._id) {
    return null;
  }

  const sourcesById = await collectTemplateCloneSources(rootTemplate);
  const idMap = new Map();
  const clonesBySourceId = new Map();

  const rootLabel = options?.label || rootTemplate.label;
  const sortedSources = Array.from(sourcesById.values()).sort((a, b) =>
    a._id === rootTemplate._id ? -1 : b._id === rootTemplate._id ? 1 : 0,
  );

  for (const source of sortedSources) {
    const params = prepareTemplateCloneParams(source, {
      label: source._id === rootTemplate._id ? rootLabel : source.label,
      callback: null,
    });
    const res = await createNewItem(params, targetParentItem);
    const createdItem = res?.data?.item;
    if (createdItem?._id) {
      idMap.set(source._id, createdItem._id);
      clonesBySourceId.set(source._id, createdItem);
      store.dispatch(saveAll({ item: createdItem, log: 'template-clone' }));
    }
  }

  for (const source of sortedSources) {
    const clone = clonesBySourceId.get(source._id);
    if (!clone?._id) {
      continue;
    }
    for (const field of REMAPPED_TEMPLATE_FIELDS) {
      if (source[field] === undefined) {
        continue;
      }
      const remappedValue = remapIdsDeep(source[field], idMap);
      await update(`items/${clone._id}`, field, remappedValue);
      clone[field] = remappedValue;
    }
    store.dispatch(saveAll({ item: clone, log: 'template-remap' }));
  }

  const rootClone = clonesBySourceId.get(rootTemplate._id) || null;
  if (typeof options?.callback === 'function') {
    options.callback(rootClone, rootTemplate, {
      idMap,
      clones: Array.from(clonesBySourceId.values()),
    });
  }
  return {
    item: rootClone,
    idMap,
    clones: Array.from(clonesBySourceId.values()),
  };
};

const GRID_TYPE_TO_ITEM_FIELD = {
  num: 'has_num',
  has_num: 'has_num',
  pic: 'pics',
  pics: 'pics',
  text: 'text',
  conditions: 'conditions',
  iframes: 'iframes',
  dates: 'dates',
  filters: 'filters',
  timeline: 'timelines',
  timelines: 'timelines',
  dice: 'dices',
  generator: 'generators',
};

const getItemEntryById = (list, entryId) => {
  if (!Array.isArray(list) || !entryId) return null;
  for (const obj of list) {
    if (getId(obj) === entryId) {
      return obj?.[entryId] || null;
    }
  }
  return null;
};

/**
 * Duplicate an existing tab item into the current item.
 * - Grid layout is copied.
 * - Any widgets coming from another item are recreated on the current item.
 * - Links/dividers are kept as-is (no widget payload).
 */
export const dupplicateTabIntoItem = async (
  tabId,
  currentItem,
  { callback } = {},
) => {
  if (!tabId || !currentItem?._id) {
    return null;
  }

  const state = store.getState();
  const cachedTab = state?.items?.[tabId];
  const shouldFetchTab =
    !cachedTab || cachedTab.__partial || !Array.isArray(cachedTab?.grid);
  const resTab = shouldFetchTab
    ? await axios.get(`${process.env.REACT_APP_API_URL}/items/${tabId}`)
    : { data: cachedTab };
  const sourceTab = resTab?.data;
  if (!sourceTab || sourceTab?.error) {
    return null;
  }

  const currentItemId = currentItem._id;
  const widgetIdMap = new Map(); // oldWidgetId -> newWidgetId
  const picIdMap = new Map(); // oldPicId -> newPicId

  const nextItemByField = {
    pics: deepClone(currentItem.pics || []),
    has_num: deepClone(currentItem.has_num || []),
    text: deepClone(currentItem.text || []),
    conditions: deepClone(currentItem.conditions || []),
    iframes: deepClone(currentItem.iframes || []),
    dates: deepClone(currentItem.dates || []),
    filters: deepClone(currentItem.filters || []),
    timelines: deepClone(currentItem.timelines || []),
    dices: deepClone(currentItem.dices || []),
    generators: deepClone(currentItem.generators || []),
  };

  const getSourceItem = async sourceItemId => {
    if (!sourceItemId) return null;
    const existing = store.getState()?.items?.[sourceItemId];
    if (existing && !existing.__partial) return existing;
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/items/${sourceItemId}`,
    );
    return res?.data?.error ? null : res?.data;
  };

  const ensureBgPic = async (sourceItem, picId) => {
    if (!picId || picId === 'none') return null;
    if (picIdMap.has(picId)) return picIdMap.get(picId);

    const sourcePic = getItemEntryById(sourceItem?.pics, picId);
    if (!sourcePic) {
      return null;
    }
    const newPicId = uuid().replaceAll('-', '');
    const clonedPic = deepClone(sourcePic) || {};
    clonedPic.i = newPicId;
    if (clonedPic.id === picId) clonedPic.id = newPicId;

    nextItemByField.pics.push({ [newPicId]: clonedPic });
    picIdMap.set(picId, newPicId);
    return newPicId;
  };

  const recreateWidget = async (sourceItem, gridType, widgetId) => {
    const field = GRID_TYPE_TO_ITEM_FIELD[gridType];
    if (!field) return null;
    const sourceEntry = getItemEntryById(sourceItem?.[field], widgetId);
    if (!sourceEntry) return null;

    const newWidgetId = uuid().replaceAll('-', '');
    const cloned = deepClone(sourceEntry) || {};
    cloned.i = newWidgetId;
    if (cloned.id === widgetId) cloned.id = newWidgetId;

    nextItemByField[field].push({ [newWidgetId]: cloned });
    widgetIdMap.set(widgetId, newWidgetId);
    return newWidgetId;
  };

  const newGrid = [];
  for (const elementObj of sourceTab?.grid || []) {
    const oldId = getId(elementObj);
    const element = elementObj?.[oldId];
    if (!oldId || !element) {
      continue;
    }

    const sourceOwnerId = element?.item;
    const gridType = element?.type;

    const sourceOwnerItem =
      sourceOwnerId && sourceOwnerId !== currentItemId
        ? await getSourceItem(sourceOwnerId)
        : null;

    let nextId = oldId;
    let nextElement = deepClone(element) || {};

    // Move all elements to the current item context for the duplicated tab.
    nextElement.item = currentItemId;

    // If the element is backed by a widget stored on another item, recreate it.
    if (
      sourceOwnerItem &&
      GRID_TYPE_TO_ITEM_FIELD[gridType] &&
      sourceOwnerId !== currentItemId
    ) {
      const recreatedId = await recreateWidget(sourceOwnerItem, gridType, oldId);
      if (recreatedId) {
        nextId = recreatedId;
        nextElement.i = recreatedId;
      }

      // If this element uses background pics from the source item, copy them too.
      if (nextElement?.bgPicId) {
        const mapped = await ensureBgPic(sourceOwnerItem, nextElement.bgPicId);
        if (mapped) nextElement.bgPicId = mapped;
      }
      if (nextElement?.bgMaskPicId) {
        const mapped = await ensureBgPic(
          sourceOwnerItem,
          nextElement.bgMaskPicId,
        );
        if (mapped) nextElement.bgMaskPicId = mapped;
      }
    }

    // Always ensure the element key matches its `i`.
    if (nextElement?.i && nextElement.i !== nextId) {
      nextElement.i = nextId;
    }

    newGrid.push({ [nextId]: nextElement });
  }

  // Persist newly created widgets on the current item before creating the tab.
  const fieldsToUpdate = Object.keys(nextItemByField).filter(fieldName => {
    const originalLen = Array.isArray(currentItem?.[fieldName])
      ? currentItem[fieldName].length
      : 0;
    return nextItemByField[fieldName].length !== originalLen;
  });
  for (const fieldName of fieldsToUpdate) {
    // eslint-disable-next-line no-await-in-loop
    await update(`items/${currentItemId}`, fieldName, nextItemByField[fieldName]);
  }

  const nextLabel = sourceTab?.label ? `${sourceTab.label}[copie]` : 'Onglet[copie]';

  const res = await createNewItem(
    {
      ...sourceTab,
      label: nextLabel,
      alias: `de ${currentItem.label}`,
      type: 'tab',
      grid: newGrid,
      callback: typeof callback === 'function' ? callback : () => null,
    },
    currentItem,
  );

  return res;
};

export const createNewItem = async (
  params = {
    label: 'new item',
    callback: () => null,
  },
  currentItem,
) => {
  const {
    label,
    alias,
    pics,
    has_num,
    dices,
    generators,
    main_pic,
    banner_pic,
    icon,
    sidebar,
    tags,
    type,
    grid,
    text,
    links,
    conditions,
    password,
    mj_only,
    tabs,
    timelines = [],
    filters,
    iframes,
    dates,
    isTemplate,
    templateSourceId,
    universe,
    world,
    campaign,
    callback,
  } = params;

  const textId = uuid().replaceAll('-', '');
  const picId = uuid().replaceAll('-', '');
  const textDefault = [
    {
      [textId]: {
        text: defaultTextNewItem,
        editing: false,
        x: 0,
        y: 0,
        w: 4,
        h: 1,
        i: textId,
      },
    },
  ];
  const picsDefault = [
    buildAvatarPicEntry(
      picId,
      resolveDefaultAvatarAssetForNewItem(
        currentItem,
        store.getState()?.items || {},
      ),
    ),
  ];

  const assign = {
    label,
    alias,
    pics: pics || picsDefault,
    has_num,
    dices,
    generators,
    main_pic,
    banner_pic,
    icon,
    sidebar,
    tags,
    type,
    grid,
    text: text || textDefault,
    links,
    conditions,
    password,
    mj_only,
    tabs,
    timelines,
    filters,
    iframes,
    dates,
    isTemplate,
    templateSourceId,
    universe,
    world,
    campaign,
  };

  if (currentItem) {
    assign.universe =
      currentItem.type === 'universe' ? currentItem._id : currentItem.universe;
    assign.world =
      currentItem.type === 'world' ? currentItem._id : currentItem.world;
    assign.campaign =
      currentItem.type === 'campaign' ? currentItem._id : currentItem.campaign;
  }

  const token = window.localStorage.getItem('auth_token');
  const headers = token
    ? { Authorization: `Bearer ${token}` }
    : undefined;

  const res = await axios
    .post(`${process.env.REACT_APP_API_URL}/items`, assign, { headers })
    .then(res => {
      if (links?.length > 0) {
        for (const objLink of links) {
          createLink(getId(objLink), res.data.item._id);
        }
      }
      if (typeof callback === 'function') {
        callback(res.data.item, currentItem);
      }
      return res;
    });

  return res;
};

export const addNum = (item, tab, callback) => {
  const clonedNum = deepCloneArray(item?.has_num);
  const i = uuid().replaceAll('-', '');
  clonedNum.push({
    [i]: {
      i: i,
      label: 'nouveau',
      value: 0,
      mod: 0,
      rounded: 'down',
      formula: null,
      display: 'big',
      xp: { min: 0, max: 0, value: 0 },
    },
  });
  update(`items/${item._id}`, 'has_num', clonedNum).then(res => {
    if (typeof callback === 'function') {
      callback(i, 'has_num', item, tab);
    }
  });
};
export const addText = (item, tab, callback) => {
  const clonedTexts = deepCloneArray(item.text);
  const i = uuid().replaceAll('-', '');
  clonedTexts.push({
    [i]: { text: 'blabla', editing: false, x: 0, y: 0, w: 4, h: 1, i: i },
  });
  update(`items/${item._id}`, 'text', clonedTexts).then(() => {
    if (tab) {
      callback(i, 'text', item, tab);
    }
  });
};
export const addCondition = (item, tab, callback) => {
  const clonedCondition = deepCloneArray(item?.conditions || []);
  const i = uuid().replaceAll('-', '');
  clonedCondition.push({
    [i]: {
      state: 'active',
      active: { label: 'bouton', condition: {}, fields: {} },
      inactive: { label: 'bouton', condition: {}, fields: {} },
      x: 0,
      y: 0,
      w: 4,
      h: 1,
      i: i,
    },
  });
  update(`items/${item._id}`, 'conditions', clonedCondition).then(() =>
    callback(i, 'conditions', item, tab),
  );
};
export const addPic = (item, tab, callback) => {
  const clonedPics = deepCloneArray(item.pics);
  const i = uuid().replaceAll('-', '');
  const defaultAvatarAsset = resolveDefaultAvatarAssetForNewItem(
    item,
    store.getState()?.items || {},
  );
  const picEntry = buildAvatarPicEntry(i, defaultAvatarAsset);
  picEntry[i] = {
    ...picEntry[i],
    w: 4,
  };
  clonedPics.push(picEntry);
  update(`items/${item._id}`, 'pics', clonedPics).then(() =>
    callback(i, 'pics', item, tab),
  );
};
export const addDivider = (item, tab, callback) => {
  const i = uuid().replaceAll('-', '');
  callback(i, 'divider', item, tab);
};
export const addLinkItem = (item, tab, linkId, callback) => {
  callback(linkId, 'link', item, tab);
};

export const addTimeline = (item, tab, callback) => {
  const clonedTimelines = deepCloneArray(item?.timelines || []);
  const i = uuid().replaceAll('-', '');
  clonedTimelines.push({
    [i]: {
      label: 'Nouvelle timeline',
      order: 'asc',
      entries: [],
      i: i,
      type: 'timeline',
      displayType: 'timeline',
    },
  });
  update(`items/${item._id}`, 'timelines', clonedTimelines).then(() =>
    callback(i, 'timeline', item, tab),
  );
};

export const addIframe = (item, tab, callback) => {
  let clonedIframes = [];
  if (item?.iframes) {
    clonedIframes = deepCloneArray(item?.iframes);
  }
  // const clonedIframes = deepCloneArray(item?.iframes) ?? [];
  const i = uuid().replaceAll('-', '');
  clonedIframes.push({
    [i]: {
      url: null,
      x: 0,
      y: 0,
      w: 4,
      h: 1,
      i: i,
    },
  });
  update(`items/${item._id}`, 'iframes', clonedIframes).then(() =>
    callback(i, 'iframes', item, tab),
  );
};

export const addFilter = (item, tab, callback) => {
  const clonedFilters = deepCloneArray(item?.filters || []);
  const i = uuid().replaceAll('-', '');
  clonedFilters.push({
    [i]: {
      label: 'filtre',
      items: [],
      x: 0,
      y: 0,
      w: 4,
      h: 2,
      i: i,
    },
  });
  update(`items/${item._id}`, 'filters', clonedFilters).then(() =>
    callback(i, 'filters', item, tab),
  );
};

export const addDice = (item, tab, callback) => {
  const clonedDice = deepCloneArray(item?.dices || []);
  const i = uuid().replaceAll('-', '');
  const mkButton = (label, min, max) => ({
    id: uuid().replaceAll('-', ''),
    label,
    count: 1,
    multiDisplay: 'total',
    resultRules: [],
    min,
    max,
    faces: [],
  });
  clonedDice.push({
    [i]: {
      label: 'd6',
      last: null,
      history: [],
      historyLimit: 10,
      buttons: [
        mkButton('d4', '1', '4'),
        mkButton('d6', '1', '6'),
        mkButton('d8', '1', '8'),
        mkButton('d10', '1', '10'),
        mkButton('d12', '1', '12'),
        mkButton('d20', '1', '20'),
        mkButton('d100', '1', '100'),
      ],
      i: i,
    },
  });
  update(`items/${item._id}`, 'dices', clonedDice).then(() =>
    callback(i, 'dice', item, tab),
  );
};

export const addGenerator = (item, tab, callback) => {
  const clonedGenerators = deepCloneArray(item?.generators || []);
  const i = uuid().replaceAll('-', '');
  const controlId = uuid().replaceAll('-', '');
  const profileId = uuid().replaceAll('-', '');
  const outputId = uuid().replaceAll('-', '');
  clonedGenerators.push({
    [i]: createDefaultGeneratorConfig({
      generatorId: i,
      controlId,
      profileId,
      outputId,
    }),
  });
  createItemGenerator(item?._id, i, clonedGenerators[clonedGenerators.length - 1]?.[i])
    .then(() => callback(i, 'generator', item, tab))
    .catch(() => {
      // Backward compatibility fallback if dedicated endpoints are unavailable.
      update(
        `items/${item._id}`,
        'generators',
        sanitizeGeneratorEntriesForStorage(clonedGenerators),
      ).then(() => callback(i, 'generator', item, tab));
    });
};

export const addDate = (item, tab, callback) => {
  const clonedDate = deepCloneArray(item?.dates || []);
  const i = uuid().replaceAll('-', '');
  clonedDate.push({
    [i]: {
      label: 'nouvel évènement',
      date: '01.01.1000',
      i: i,
    },
  });
  update(`items/${item._id}`, 'dates', clonedDate).then(() =>
    callback(i, 'dates', item, tab),
  );
};

export const getSearch = async (
  value,
  item,
  searchIn,
  listCampaignsId,
  options = {},
) => {
  const universeId = item?.type === 'universe' ? item._id : item.universe;

  const searchInUniverse = universeId;
  const searchInWold =
    item?.type === 'world' ? item._id : item?.world ? item.world : undefined;

  let arrCampaigns = Array.isArray(listCampaignsId)
    ? [...listCampaignsId.filter(Boolean)]
    : null;

  if (
    (item?.type === 'universe' || item?.type === 'world') &&
    Array.isArray(arrCampaigns)
  ) {
    arrCampaigns = [...arrCampaigns, 'none-visitor'];
  }

  const searchInType = searchIn.type !== 'tab' ? undefined : searchIn.type;
  const params = new URLSearchParams();
  if (searchInType !== undefined) {
    params.set('type', searchInType);
  }
  if (searchInUniverse !== undefined) {
    params.set('universe', searchInUniverse);
  }
  if (searchInWold !== undefined) {
    params.set('world', searchInWold);
  }
  // If campaigns are not hydrated yet, omit this param to let backend search
  // across the whole accessible universe scope.
  if (Array.isArray(arrCampaigns)) {
    params.set('campaign', JSON.stringify(arrCampaigns));
  }
  if (options?.isTemplate === true) {
    params.set('isTemplate', 'true');
  }

  const searchUrl = `${process.env.REACT_APP_API_URL}/items/search/${value}?${params.toString()}`;
  const res = await axios.get(searchUrl);
  if (Array.isArray(res.data)) {
    return res.data;
  } else {
    return [];
  }
};

export const getTemplatesLibrary = async () => {
  const token = window.localStorage.getItem('auth_token');
  if (!token) {
    return { templates: [], targets: [] };
  }
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/items/templates`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return {
    templates: Array.isArray(res?.data?.templates) ? res.data.templates : [],
    targets: Array.isArray(res?.data?.targets) ? res.data.targets : [],
  };
};

export const addInTab = (idToAdd, type, item, tab) => {
  const normalizedType = type === 'timelines' ? 'timeline' : type;
  const clonedGrid = deepCloneArray(tab.grid);
  const displayType =
    normalizedType === 'timeline'
      ? 'horizontal'
      : normalizedType === 'dice'
        ? 'simple'
        : normalizedType === 'generator'
          ? 'generator'
          : normalizedType;
  clonedGrid.push({
    [idToAdd]: {
      item: item._id,
      type: normalizedType,
      displayType,
      x: 0,
      y: 0,
      w: 12,
      h: 2,
      i: idToAdd,
    },
  });
  update(`items/${tab._id}`, 'grid', clonedGrid);
};

const applyOptimisticUpdate = (path, type, value) => {
  if (!path?.startsWith('items/')) {
    return;
  }
  const itemId = path.split('/')[1];
  if (!itemId) {
    return;
  }
  const state = store.getState();
  const existing = state?.items?.[itemId];
  if (!existing) {
    return;
  }
  const optimisticAt = {
    ...(existing.__optimisticAt || {}),
    [type]: Date.now(),
  };

  let nextItem = {
    ...existing,
    __optimisticAt: optimisticAt,
  };

  if (typeof type === 'string' && type.includes('.')) {
    const pathParts = type.split('.').filter(Boolean);
    if (pathParts.length > 0) {
      // Clone the nested chain so redux sees new references at every updated level.
      let cursor = nextItem;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const key = pathParts[i];
        const prevValue = cursor?.[key];
        cursor[key] =
          prevValue && typeof prevValue === 'object' && !Array.isArray(prevValue)
            ? { ...prevValue }
            : {};
        cursor = cursor[key];
      }
      cursor[pathParts[pathParts.length - 1]] = value;
    }
  } else {
    nextItem[type] = value;
  }

  store.dispatch(saveAll({ item: nextItem, log: 'optimistic update' }));
};

export const update = async (path, type, value) => {
  applyOptimisticUpdate(path, type, value);
  const res = await axios.put(`${process.env.REACT_APP_API_URL}/${path}`, {
    type,
    value,
  });
  return res;
};

export const updateField = (tab, tabType, value, id, field) => {
  const latestState = store.getState();
  const latestTab = latestState?.items?.[tab?._id] || tab;
  const clonedGrid = deepCloneArray(latestTab?.grid);
  let idx = -1;
  for (let i = 0; i < clonedGrid.length; i++) {
    const element = clonedGrid[i];
    if (getId(element) === id) {
      idx = i;
    }
  }
  if (idx > -1) {
    if (tabType === 'map') {
      const map = clonedGrid[idx][id]?.map ?? {};
      clonedGrid[idx][id] = { ...clonedGrid[idx][id], ...map };
      clonedGrid[idx][id].map = {
        ...map,
        [field]: field === 'value' ? +value : value,
      };
    } else {
      clonedGrid[idx][id][field] = field === 'value' ? +value : value;
    }
    if (value === 'hauteur fixe') {
      clonedGrid[idx][id].isResizable = true;
    }
    if (value === 'hauteur dynamique') {
      clonedGrid[idx][id].isResizable = false;
    }
    update(`items/${latestTab._id}`, 'grid', clonedGrid);
  }
};
